import { SetStateAction } from 'jotai'
import { Dispatch, useState } from 'react'
import { Controller, ControllerRenderProps, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { CatAndSubCatDefaultCategoryFragment } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { Button } from '@/modules/shared/components/button/Button'
import ComboboxServer from '@/modules/shared/components/combobox/server/ComboboxServer'
import { Tooltip } from '@/modules/shared/components/tooltip/Tooltip'
import { QuestionMarkIcon } from '@/modules/shared/icons/QuestionMarkIcon'
import { TrashIcon } from '@/modules/shared/icons/TrashIcon'
import { checkNetworkStatus } from '@/modules/shared/utils/checkNetworkStatus'
import { useGetCatAndSubCatCategories } from '@/modules/suppliers/pages/supplier/general/settings/form-elements/useGetCatAndSubCatCategories'
import { useGetCatAndSubCatSubCategories } from '@/modules/suppliers/pages/supplier/general/settings/form-elements/useGetCatAndSubCatSubCategories'

interface CatAndSubCatProps {
  defaultCategory?: CatAndSubCatDefaultCategoryFragment | null
  setRemoveDefaultCategory?: Dispatch<SetStateAction<boolean>>
  removeDefaultCategory?: boolean
  title: string
  description: string
  register: string
  setCategoryObject?: Dispatch<SetStateAction<CatAndSubCatDefaultCategoryFragment | null>>
  disabled?: boolean
  shouldDirtyOnRemove?: boolean
}

export default function CatAndSubCat({
  defaultCategory,
  setRemoveDefaultCategory,
  removeDefaultCategory,
  title,
  description,
  register,
  setCategoryObject,
  disabled,
  shouldDirtyOnRemove = false,
}: CatAndSubCatProps) {
  const { t } = useTranslation()
  const [categoryId, setCategoryId] = useState<number | null>(null)

  const { control, setValue } = useFormContext()

  const {
    categories,
    networkStatus: categoriesNetworkStatus,
    refetch: refetchingCategories,
    onFetchMore: fetchMoreCategories,
    pageInfo: pageInfoCategories,
  } = useGetCatAndSubCatCategories()

  const {
    fetchSubCategories,
    subCategories,
    networkStatus: subCategoriesNetworkStatus,
    refetch: refetchingSubCategories,
    onFetchMore: onFetchMoreSubCategories,
    pageInfo: pageInfoSubCategories,
  } = useGetCatAndSubCatSubCategories({ parentId: Number(categoryId) })

  const onCategorySelect = (id: number) => {
    setCategoryId(id)
    fetchSubCategories({ variables: { filters: { parentId: id } } })
  }

  const onSelectSubCategory = (field: ControllerRenderProps, category: CatAndSubCatDefaultCategoryFragment) => {
    field.onChange(category.id)
    const parentCategory = categories.find((c) => c.id === categoryId)
    setCategoryObject?.({ ...category, parent: parentCategory ?? null })
  }

  const onRemoveDefaultCategory = () => {
    setValue(register, null, { shouldDirty: shouldDirtyOnRemove })
    setRemoveDefaultCategory?.(true)
  }

  return (
    <section>
      <h2 className="font-semibold">{title}</h2>
      <p className="text-gray-500">{description}</p>
      <div className="mt-4 space-y-3">
        {defaultCategory && !removeDefaultCategory && (
          <section
            className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-gray-200 p-3"
            data-testid="default-category"
          >
            {(defaultCategory?.parent ? `${defaultCategory?.parent?.name} > ` : '') + defaultCategory?.name}
            <div className="flex items-center gap-x-1 text-gray-500">
              <span>{t('general.currentlySet', 'Currently Set')}</span>
              <Tooltip
                content={t(
                  'mySuppliers.supplier.defaultCategoryCurrentlySetTooltip',
                  'The Category and Sub-Category currently set.'
                )}
              >
                <QuestionMarkIcon className="size-4" />
              </Tooltip>
              {!disabled && (
                <Tooltip
                  content={t(
                    'mySuppliers.supplier.removeDefaultCategoryCurrentlySetTooltip',
                    'Remove the currently set Category and Sub-Category.'
                  )}
                >
                  <Button
                    type="button"
                    data-testid="remove-default-category"
                    className="flex items-center justify-center"
                    onClick={() => !disabled && onRemoveDefaultCategory()}
                  >
                    <TrashIcon className="size-7" />
                  </Button>
                </Tooltip>
              )}
            </div>
          </section>
        )}
        {(removeDefaultCategory || !defaultCategory) && (
          <>
            <ComboboxServer
              testId="categories"
              placeholder={t('general.selectACategory', 'Select a Category')}
              onDisplay={(e) => `${e.name}`}
              onInputChange={(e) => refetchingCategories({ filters: { searchText: e, hasParent: false } })}
              networkStatus={categoriesNetworkStatus}
              keyExtractor={(e) => String(e.id)}
              items={categories}
              onSelected={(e) => onCategorySelect(e.id)}
              onFetchMore={fetchMoreCategories}
              hasMore={!!pageInfoCategories?.hasNextPage}
              disabled={disabled}
            />
            <Controller
              control={control}
              name={register}
              render={({ field }) => (
                <ComboboxServer
                  testId="sub-categories"
                  placeholder={t('general.selectASubCategory', 'Select a Sub-Category')}
                  onDisplay={(e) => `${e.name}`}
                  onInputChange={(e) =>
                    categoryId != null &&
                    refetchingSubCategories({ filters: { parentId: Number(categoryId), searchText: e } })
                  }
                  networkStatus={subCategoriesNetworkStatus}
                  keyExtractor={(e) => String(e.id)}
                  items={subCategories}
                  loading={checkNetworkStatus(subCategoriesNetworkStatus).setVariablesLoading}
                  resetSelectItem={categoryId}
                  onSelected={(e) => onSelectSubCategory(field, e)}
                  onFetchMore={onFetchMoreSubCategories}
                  hasMore={!!pageInfoSubCategories?.hasNextPage}
                  disabled={!categoryId || disabled}
                />
              )}
            />
          </>
        )}
      </div>
    </section>
  )
}
